import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Mail,
  Server,
  Lock,
  User,
  Send,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Eye,
  EyeOff,
  Save
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import api from '../../lib/axios';

const SmtpSettingsPage = () => {
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  const [settings, setSettings] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    sender_email: '',
    sender_name: '',
    use_tls: true,
    enabled: false
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/settings/smtp');
      setSettings(res.data);
    } catch (error) {
      console.error('Error fetching SMTP settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/settings/smtp', settings);
      toast.success('Configuration SMTP enregistrée !');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testEmail) {
      toast.error('Veuillez entrer une adresse email de test');
      return;
    }
    setTesting(true);
    try {
      const res = await api.post('/settings/smtp/test', { test_email: testEmail });
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du test');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />

      <main className={cn("transition-all duration-300 px-6 md:px-8 pb-6 md:pb-8", collapsed ? "md:ml-20" : "md:ml-64")}
        style={{ paddingTop: "130px" }}
      >
        <div className="max-w-2xl">
          <div className="mb-6">
            <h1 className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>Configuration SMTP</h1>
            <p className="text-sm text-slate-500">Configurez votre serveur de messagerie pour l'envoi d'emails</p>
          </div>

          <Card className="card mb-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <CardTitle>Serveur SMTP</CardTitle>
                    <CardDescription>PlanetHoster ou autre fournisseur</CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">Activé</span>
                  <Switch
                    checked={settings.enabled}
                    onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Server Settings */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="flex items-center gap-2">
                    <Server className="w-4 h-4" />
                    Serveur SMTP
                  </Label>
                  <Input
                    value={settings.smtp_host}
                    onChange={(e) => setSettings({ ...settings, smtp_host: e.target.value })}
                    placeholder="mail.planethoster.com"
                  />
                </div>
                <div>
                  <Label>Port</Label>
                  <Input
                    type="number"
                    value={settings.smtp_port}
                    onChange={(e) => setSettings({ ...settings, smtp_port: parseInt(e.target.value) })}
                    placeholder="587"
                  />
                </div>
              </div>

              {/* Authentication */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Utilisateur
                  </Label>
                  <Input
                    value={settings.smtp_user}
                    onChange={(e) => setSettings({ ...settings, smtp_user: e.target.value })}
                    placeholder="contact@votredomaine.com"
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-2">
                    <Lock className="w-4 h-4" />
                    Mot de passe
                  </Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={settings.smtp_password}
                      onChange={(e) => setSettings({ ...settings, smtp_password: e.target.value })}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>

              {/* Sender Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Email expéditeur</Label>
                  <Input
                    type="email"
                    value={settings.sender_email}
                    onChange={(e) => setSettings({ ...settings, sender_email: e.target.value })}
                    placeholder="noreply@votredomaine.com"
                  />
                </div>
                <div>
                  <Label>Nom expéditeur</Label>
                  <Input
                    value={settings.sender_name}
                    onChange={(e) => setSettings({ ...settings, sender_name: e.target.value })}
                    placeholder="MSC Agency"
                  />
                </div>
              </div>

              {/* TLS */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium text-sm">Utiliser TLS/STARTTLS</p>
                  <p className="text-xs text-slate-500">Recommandé pour la sécurité (port 587)</p>
                </div>
                <Switch
                  checked={settings.use_tls}
                  onCheckedChange={(checked) => setSettings({ ...settings, use_tls: checked })}
                />
              </div>

              {/* Save Button */}
              <Button onClick={handleSave} disabled={saving} className="w-full" style={{ backgroundColor: theme.colors?.accent }}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Enregistrer la configuration
              </Button>
            </CardContent>
          </Card>

          {/* Test Section */}
          <Card className="card">
            <CardHeader>
              <CardTitle className="text-base">Tester la configuration</CardTitle>
              <CardDescription>Envoyez un email de test pour vérifier que tout fonctionne</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-3">
                <Input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="votre@email.com"
                  className="flex-1"
                />
                <Button onClick={handleTest} disabled={testing || !settings.enabled}>
                  {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Envoyer test
                </Button>
              </div>
              {!settings.enabled && (
                <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Activez le SMTP pour pouvoir envoyer des emails
                </p>
              )}
            </CardContent>
          </Card>

          {/* Help */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <h3 className="font-medium text-blue-900 mb-2">Configuration PlanetHoster</h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Serveur SMTP : <code className="bg-blue-100 px-1 rounded">mail.votredomaine.com</code></li>
              <li>• Port : <code className="bg-blue-100 px-1 rounded">587</code> (TLS) ou <code className="bg-blue-100 px-1 rounded">465</code> (SSL)</li>
              <li>• Utilisateur : Votre adresse email complète</li>
              <li>• Authentification : Mot de passe de votre boîte email</li>
            </ul>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SmtpSettingsPage;
